import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import '@adornis/calendar/calendar.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { DoesNotExistAspect } from '@adornis/filter/aspect-schemas/ExistsAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-data-view-table.js';
import '@chancenwerk/design/components/cr-dropdown-search-selection.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { WorkTrackingAPI } from '@chancenwerk/work-tracking/api/WorkTrackingAPI.js';
import { WorkTrackingAuslage } from '@chancenwerk/work-tracking/entitites/WorkTrackingAuslage.js';
import { WorkTrackingEntry } from '@chancenwerk/work-tracking/entitites/WorkTrackingEntry.js';
import { WorkTrackingTaetigkeit } from '@chancenwerk/work-tracking/entitites/WorkTrackingTaetigkeit.js';
import { WorkTrackingTaetigkeitenState } from '@chancenwerk/work-tracking/entitites/WorkTrackingTaetigkeitenState.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { DateTime } from 'luxon';
import { switchMap, tap } from 'rxjs';
import { ChromiumDialog } from '../../client/design/components/cr-dialog.js';
import { Role } from '../../db/semantics/Role.js';
import {
  addAuslageAction,
  addSingleWorkAction,
  addWorkAction,
  confirmAllAction,
  disableButton,
  editButton,
  finishedButton,
  historyButton,
  questionButton,
} from './actions.js';
import {
  createActionButtons,
  createAuslageActionButtons,
  createAuslageKpfColumn,
  createAuslageLastNoteColumn,
  createKpfColumn,
  createLastNoteColumn,
  createMoneyColumn,
  createPrefixColumns,
  createReceiptColumn,
  createTimeColumn,
  workTrackingStateTemplate,
} from './data-view.js';

/**
 * @customElement chancen-component-work-tracking
 */
@customElement('chancen-component-work-tracking')
export class ChancenComponentWorkTracking extends ChemistryLitElement {
  @property() eid = new RXController<Maybe<string>>(this, null);

  @state() protected readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());
  @state() _entry = new RXController(
    this,
    this.eid.observable.pipe(
      switchMap(eid =>
        subscribeByID(
          WorkTrackingEntry,
          eid ?? '',
        )({
          _id: 1,
          user: {
            _id: 1,
            vorname: 1,
            nachname: 1,
            email: 1,
            adresse: {
              zipCity: {
                city: 1,
                zip: 1,
              },
              strasse: 1,
              country: 1,
            },
            kontoInformationen: {
              iban: 1,
              kontoinhaber: 1,
              kreditinstitut: 1,
              bic: 1,
            },
            unterlagen: {
              vertragErhalten: 1,
              selbstverplichtungserklaerungErhalten: 1,
              datenschutzerklaerungErhalten: 1,
              fuehrungzeugnisGueltigAb: 1,
            },
          },
          date: 1,
          state: 1,
          moneySum: 1,
          moneyAuslagenSum: 1,
          mentionedKpfIDs: 1,
          mentionedKpfNames: 1,
          mentionedKpfs: 1,
        }),
      ),
      tap(e => {
        if (e?.user) {
          this.dispatchEvent(
            new CustomEvent('title-change', {
              detail: { value: `Tätigkeiten Übersicht: ${e.date?.toFormat('MM')} ${e.user.toString()}` },
            }),
          );
          WorkTrackingAPI.populateEntry(e._id)().then(console.log).catch(console.error);
        }
      }),
    ),
  );

  private _fetchAgain() {
    this.eid.next(this.eid.value);
  }

  private _canSeeAll() {
    return !!(
      this._isSelf() ||
      this._currentUser.value?.roles.some(r => r.name === Role.INTERNAL_KPF_OPERATIV || r.name === Role.ADMIN) ||
      this._currentUser.value?.hasPermission(ChancenPermission.board.EDIT_WORK_TRACKING_EVERYTHING, ANY_CONTEXT)
    );
  }

  private _isSelf() {
    return this._currentUser.value?._id === this._entry.value?.user?._id;
  }

  private _outsideEntryDate(date: DateTime) {
    if (!this._entry.value?.date) {
      return true;
    }

    return date < this._entry.value.date.startOf('month') || date > this._entry.value.date.endOf('month');
  }

  private _extendedInfo() {
    if (!this._canSeeAll()) {
      return;
    }

    return html`
      <cr-text bold>Adresse:</cr-text> <cr-text>${this._entry.value?.user?.adresse?.toString()}</cr-text>
      <cr-flex horizontal crossaxis-center space="sm">
        <cr-text bold>Konto Informationen:</cr-text>
        <cr-button
          mode="icon"
          icon="edit"
          @click=${() => {
            const form = new FormController(this, this._entry.value?.user, {
              onSuccess: doc => {
                if (!this._entry.value) {
                  return;
                }
                doc.save();
                this._entry.value.user = doc;
                this.requestUpdate();
              },
            });
            ChromiumDialog.showPopup<void>({
              content: resolve => {
                return html` <cr-flex space="md" padding="md">
                  <cr-input
                    ${form.field('kontoInformationen', 'kontoinhaber')}
                    placeholder="Kontoinhaber:in"
                  ></cr-input>
                  <cr-flex horizontal space="md" crossaxis-end>
                    <cr-input
                      ${form.field('kontoInformationen', 'iban')}
                      flex
                      type="iban"
                      placeholder="IBAN"
                    ></cr-input>
                    <cr-input ${form.field('kontoInformationen', 'bic')} placeholder="BIC"></cr-input>
                  </cr-flex>
                  <cr-input
                    ${form.field('kontoInformationen', 'kreditinstitut')}
                    placeholder="Kreditinstitut"
                  ></cr-input>
                  <cr-button
                    @click=${() => {
                      form.validate();
                      resolve();
                    }}
                    >Speichern</cr-button
                  >
                </cr-flex>`;
              },
            });
          }}
        ></cr-button>
      </cr-flex>
      <cr-flex space="sm">
        ${[
          this._entry.value?.user?.kontoInformationen.kontoinhaber ?? null,
          this._entry.value?.user?.kontoInformationen.iban ?? 'IBAN fehlt',
          this._entry.value?.user?.kontoInformationen.bic ?? 'BIC fehlt',
          this._entry.value?.user?.kontoInformationen.kreditinstitut ?? null,
        ]
          .filter(Boolean)
          .map(t => {
            return html`<cr-text>${t}</cr-text>`;
          })}
      </cr-flex>
    `;
  }

  private _extendedInfoRight() {
    if (!this._canSeeAll()) {
      return;
    }

    const yesNo = (value: boolean) =>
      value
        ? html`<cr-text ${css({ color: this.colors.tone.success })}>Ja</cr-text>`
        : html`<cr-text ${css({ color: this.colors.tone.error })} bold>Nein</cr-text>`;

    return html` <cr-grid columns="300px 1fr" space="md">
      <cr-text bold>Vertrag erhalten:</cr-text>
      ${yesNo(!!this._entry.value?.user?.unterlagen?.vertragErhalten)}
      <cr-text bold>Selbstverpflichtungserklärung erhalten:</cr-text>
      ${yesNo(!!this._entry.value?.user?.unterlagen?.selbstverplichtungserklaerungErhalten)}
      <cr-text bold>Datenschutzerklärung erhalten:</cr-text>
      ${yesNo(!!this._entry.value?.user?.unterlagen?.datenschutzerklaerungErhalten)}
      <cr-text bold>Führungszeugnis gültig:</cr-text>
      ${yesNo(!!this._entry.value?.user?.unterlagen?.fuehrungzeugnisGueltigAb)}
    </cr-grid>`;
  }

  override render() {
    if (this._entry.isLoading) {
      return html`<cr-loader></cr-loader>`;
    }

    if (!this._entry.value) {
      return html`<cr-flex space="md" padding="sm">
        <cr-text>Keine Tätigkeiten gefunden</cr-text>
      </cr-flex>`;
    }

    return html`
      <cr-flex space="md" padding="xs">
        <cr-card space="md" padding="md">
          <chancen-component-card-title>Übersicht</chancen-component-card-title>
          <cr-flex horizontal space="xxl">
            <cr-flex space="md">
              <cr-grid columns="300px 1fr" space="md">
                <cr-text bold>Person:</cr-text>
                <cr-text>${this._entry.value?.user?.toString()}</cr-text>
                ${this._extendedInfo()}
                <cr-text bold>Zeitraum:</cr-text>
                <cr-text>
                  ${this._entry.value?.date?.startOf('month').toFormat('dd.MM.yyyy')} -
                  ${this._entry.value?.date?.endOf('month').toFormat('dd.MM.yyyy')}
                </cr-text>
                <cr-text bold>Aufwandsentschädigung insg.:</cr-text>
                <cr-text>${this._entry.value?.moneySum?.toFixed(2)}€</cr-text>
                <cr-text bold>Auslagen:</cr-text>
                <cr-text>${this._entry.value?.moneyAuslagenSum?.toFixed(2)}€</cr-text>
                <cr-text bold>Status:</cr-text>
                ${workTrackingStateTemplate(this, this._entry.value?.state ?? WorkTrackingTaetigkeitenState.OPEN)}
              </cr-grid>
            </cr-flex>
            <cr-flex space="md"> ${this._extendedInfoRight()} </cr-flex>
          </cr-flex>
        </cr-card>
        ${this._canSeeAll()
          ? html`
              <cr-card space="md" padding="md">
                <chancen-component-card-title>Einzeltätigkeiten</chancen-component-card-title>
                ${this._singleWork()}
              </cr-card>
            `
          : nothing}
        <cr-card space="md" padding="md">
          <chancen-component-card-title>Tätigkeiten</chancen-component-card-title>
          ${this._work()}
        </cr-card>
        ${this._canSeeAll()
          ? html`
              <cr-card space="md" padding="md">
                <chancen-component-card-title>Auslagen</chancen-component-card-title>
                ${this._auslagen()}
              </cr-card>
            `
          : nothing}
      </cr-flex>
    `;
  }

  private _finishedButton(t: WorkTrackingTaetigkeit | WorkTrackingAuslage) {
    return finishedButton(this, t, this._currentUser.value?._id ?? '');
  }

  private _questionButton(t: WorkTrackingTaetigkeit | WorkTrackingAuslage) {
    return questionButton(this, t, this._currentUser.value?._id ?? '');
  }

  private _editButton(t: WorkTrackingTaetigkeit | WorkTrackingAuslage) {
    return editButton(t, this._currentUser.value?._id ?? '', () => this._fetchAgain());
  }

  private _disableButton(t: WorkTrackingTaetigkeit) {
    return disableButton(this, t, this._currentUser.value?._id ?? '', () => this._fetchAgain());
  }

  private _historyButton(t: WorkTrackingTaetigkeit | WorkTrackingAuslage) {
    return historyButton(this, t);
  }

  private _singleWork() {
    return html`
      <cr-flex>
        <x-data-view
          .options=${{
            class: WorkTrackingTaetigkeit,
            operation: WorkTrackingAPI.subscribeTaetigkeiten,
            selectionSet: WorkTrackingTaetigkeit.allFields,
            view: new View({
              filter: new AdornisFilter({
                aspects: [
                  new BooleanAspect({ fieldName: 'isSingle', value: true }),
                  new StringAspect({ fieldName: 'userID', value: this._entry.value?.user?._id }),
                  new DateTimeAspect({
                    fieldName: 'date',
                    firstValue: this._entry.value?.date?.startOf('month').toJSDate(),
                    secondValue: this._entry.value?.date?.endOf('month').toJSDate(),
                  }),
                ],
              }),
            }),
            isEntryClickable: false,
            onlyShowLastTranslation: true,
            customColumns: [
              ...createPrefixColumns<WorkTrackingTaetigkeit>(this, () => this._isSelf()),
              { fieldName: 'type', columnName: 'Art' },
              createKpfColumn(),
              createMoneyColumn<WorkTrackingTaetigkeit>(this),
              {
                customID: 'lastNote',
                columnName: 'Letzte Notiz',
                columnWidth: '300px',
                getFieldMapping: e => e.lastNote,
              },
              createActionButtons(
                this,
                t => this._finishedButton(t),
                t => this._questionButton(t),
                t => this._editButton(t),
                t => this._disableButton(t),
                t => this._historyButton(t),
                () => this._canSeeAll(),
              ),
            ],
          } as DataViewOptions<typeof WorkTrackingTaetigkeit>}
        >
        </x-data-view>
        <cr-flex
          space="sm"
          ${css({
            paddingTop: this.spacing.md,
            borderTop: `1px solid ${this.colors.light}`,
          })}
        >
          <cr-button
            ${css({ maxWidth: '200px' })}
            @click=${addSingleWorkAction(
              this._entry.value?.user?._id,
              () => this._isSelf(),
              date => this._outsideEntryDate(date),
              () => this._fetchAgain(),
            )}
          >
            <cr-tooltip><cr-text>Fehlt ein Eintrag?</cr-text></cr-tooltip>
            Hinzufügen
          </cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _work() {
    return html`
      <cr-flex>
        <x-data-view
          .options=${{
            class: WorkTrackingTaetigkeit,
            operation: WorkTrackingAPI.subscribeTaetigkeiten,
            selectionSet: WorkTrackingTaetigkeit.allFields,
            view: new View({
              filter: new AdornisFilter({
                aspects: [
                  new DoesNotExistAspect({ fieldName: 'isSingle' }),
                  new StringAspect({ fieldName: 'userID', value: this._entry.value?.user?._id }),
                  new DateTimeAspect({
                    fieldName: 'date',
                    firstValue: this._entry.value?.date?.startOf('month').toJSDate(),
                    secondValue: this._entry.value?.date?.endOf('month').toJSDate(),
                  }),
                ],
              }),
            }),
            isEntryClickable: false,
            onlyShowLastTranslation: true,
            customColumns: [
              ...createPrefixColumns<WorkTrackingTaetigkeit>(this, () => this._isSelf()),
              createTimeColumn(),
              { fieldName: 'type', columnName: 'Art' },
              createKpfColumn(),
              createMoneyColumn<WorkTrackingTaetigkeit>(this),
              createLastNoteColumn(),
              createActionButtons(
                this,
                t => this._finishedButton(t),
                t => this._questionButton(t),
                t => this._editButton(t),
                t => this._disableButton(t),
                t => this._historyButton(t),
                () => this._canSeeAll(),
              ),
            ],
          } as DataViewOptions<typeof WorkTrackingTaetigkeit>}
        >
        </x-data-view>
        <cr-flex
          horizontal
          space="sm"
          ${css({
            paddingTop: this.spacing.md,
            borderTop: `1px solid ${this.colors.light}`,
          })}
        >
          <cr-button
            ${css({ maxWidth: '200px' })}
            @click=${addWorkAction(
              this._entry.value?.user?._id,
              () => this._isSelf(),
              date => this._outsideEntryDate(date),
              () => this._fetchAgain(),
            )}
          >
            <cr-tooltip><cr-text>Fehlt ein Eintrag?</cr-text></cr-tooltip>
            Hinzufügen
          </cr-button>
          <cr-button
            ${css({ maxWidth: '200px' })}
            icon="check"
            @click=${confirmAllAction(this._entry.value?.user?._id, () => this._isSelf())}
          >
            Stimmt alles
          </cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _auslagen() {
    return html`
      <cr-flex>
        <x-data-view
          .options=${{
            class: WorkTrackingAuslage,
            operation: WorkTrackingAPI.subscribeAuslagen,
            view: new View({
              filter: new AdornisFilter({
                aspects: [
                  new StringAspect({ fieldName: 'userID', value: this._entry.value?.user?._id }),
                  new DateTimeAspect({
                    fieldName: 'date',
                    firstValue: this._entry.value?.date?.startOf('month').toJSDate(),
                    secondValue: this._entry.value?.date?.endOf('month').toJSDate(),
                  }),
                ],
              }),
            }),
            isEntryClickable: false,
            onlyShowLastTranslation: true,
            customColumns: [
              ...createPrefixColumns<WorkTrackingAuslage>(this, () => this._isSelf()),
              { fieldName: 'type', columnName: 'Art' },
              { fieldName: 'note', columnName: 'Was' },
              createAuslageKpfColumn(),
              createReceiptColumn(this, () => this._currentUser.value?._id),
              createMoneyColumn<WorkTrackingAuslage>(this),
              createAuslageLastNoteColumn(),
              createAuslageActionButtons(
                this,
                t => this._finishedButton(t),
                t => this._questionButton(t),
                t => this._editButton(t),
                t => this._historyButton(t),
              ),
            ],
          } as DataViewOptions<typeof WorkTrackingAuslage>}
        >
        </x-data-view>
        <cr-flex
          space="sm"
          ${css({
            paddingTop: this.spacing.md,
            borderTop: `1px solid ${this.colors.light}`,
          })}
        >
          <cr-button
            ${css({ maxWidth: '200px' })}
            @click=${addAuslageAction(
              this._entry.value?.user?._id,
              () => this._isSelf(),
              date => this._outsideEntryDate(date),
              () => this._fetchAgain(),
            )}
          >
            <cr-tooltip><cr-text>Fehlt ein Eintrag?</cr-text></cr-tooltip>
            Hinzufügen
          </cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }
}
